import { useId } from 'react';
import {
  getBatchTestDocumentUrl,
  usePublicBatchTestResults,
  resolvePublicLabTestStatus,
  type PublicBatchTestResultsState,
  type BatchTestAnalyte,
  type BatchTestCategory,
  type PotencyDisplayPolicy,
} from '../../hooks/usePublicBatchTestResults';
import { formatDate } from '../../utils/formatDate';
import ExpandedSection from './ExpandedSection';
import './batchTestResultsSection.css';

type Props = { issuanceId: string; index: number; chevronSrc?: string; labTestStatus?: string; marketCode?: string };
type DisplayProps = { selectedAnalyteKeys?: readonly string[]; hideTotals?: boolean };

function isTotal(analyte: BatchTestAnalyte): boolean {
  const name = analyte.name?.normalize('NFKC').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim() ?? '';
  return (analyte.aggregateKind === 'total' && analyte.analyteKey !== 'totalTerpenes')
    || /^total(?:thc|cbd|cbc|cannabinoids?)$/i.test(analyte.analyteKey ?? '')
    || /^total\s*(?:thc|cbd|cbc|cannabinoids?)(?:\s|$)/.test(name);
}

function displayedBatches(category: BatchTestCategory, selectedAnalyteKeys?: readonly string[], hideTotals = false) {
  if (!hideTotals && (category.kind !== 'potency' || selectedAnalyteKeys === undefined)) return category.batches;
  return category.batches.map(batch => ({ ...batch, analytes: batch.analytes.filter(analyte =>
    (!hideTotals || !isTotal(analyte)) && (category.kind !== 'potency' || selectedAnalyteKeys === undefined || selectedAnalyteKeys.includes(analyte.analyteKey ?? '')))
  })).filter(batch => batch.analytes.length > 0);
}

function hiddenAdverseResults(category: BatchTestCategory, batches: BatchTestCategory['batches']) {
  const visibleRows = new Set(batches.flatMap(batch => batch.analytes));
  let failed = false;
  let revoked = false;
  for (const batch of category.batches) {
    const hiddenRows = batch.analytes.filter(analyte => !visibleRows.has(analyte));
    if (hiddenRows.length === 0) continue;
    const hiddenBatch = hiddenRows.length === batch.analytes.length;
    failed ||= (hiddenBatch && batch.overallPassed === false)
      || hiddenRows.some(analyte => analyte.passed === false);
    revoked ||= (hiddenBatch && batch.isRevoked === true)
      || hiddenRows.some(analyte => analyte.isRevoked === true || analyte.detailIsRevoked === true);
  }
  if (!failed && !revoked) return null;
  const status = failed && revoked ? 'failed and revoked' : failed ? 'failed' : 'revoked';
  return <p role="status" data-testid="landing-hidden-adverse-results">
    Reported lab results include {status} results not displayed in this view.
  </p>;
}

function displaySelection(policy: PotencyDisplayPolicy | null | undefined, marketCode?: string) {
  return marketCode?.trim().toUpperCase() === 'CO' && policy?.marketCode === 'CO'
    ? policy.selectedAnalyteKeys : undefined;
}

function analyteName(analyte: BatchTestAnalyte): string {
  const unit = analyte.unit?.trim();
  if (!unit) return analyte.name;
  const escapedUnit = unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return analyte.name.replace(new RegExp(`(?:\\s*\\(${escapedUnit}\\)(?=\\s|$)|\\s+${escapedUnit}\\s*$)`, 'i'), '').trim() || analyte.name;
}

function analyteValue(analyte: BatchTestAnalyte, potency: boolean): string {
  if (analyte.value === null || analyte.mappingStatus === 'unitUnavailable' || analyte.mappingStatus === 'identityMismatch'
    || (potency && (!analyte.unit?.trim() || analyte.mappingStatus === 'unmapped'))) return 'Unavailable';
  return String(analyte.value);
}

function analyteStatus(analyte: BatchTestAnalyte): string {
  if (analyte.isRevoked === true || analyte.detailIsRevoked === true) return 'Revoked';
  if (analyte.passed === true) return 'Passed';
  if (analyte.passed === false) return 'Failed';
  return analyte.informationalOnly === true ? 'Informational' : 'Unavailable';
}

function Category({ category, issuanceId, index, selectedAnalyteKeys, hideTotals }: Props & DisplayProps & { category: BatchTestCategory }) {
  const headingId = useId();
  const selectedCount = displayedBatches(category, selectedAnalyteKeys, hideTotals).reduce((count, batch) => count + batch.analytes.length, 0);
  const displayFiltered = (category.kind === 'potency' && (hideTotals || selectedAnalyteKeys !== undefined))
    || (hideTotals && category.batches.some(batch => batch.analytes.some(isTotal)));
  return (
    <details aria-labelledby={headingId} className="rid-batch-results__category" data-testid="landing-test-category">
      <summary className="rid-batch-results__category-header">
        <h4 id={headingId}>{category.name}</h4>
        {category.status && <span className="rid-batch-results__category-summary">
          {category.requirement && <>{category.requirement === 'required' ? 'Required' : category.requirement === 'mayBeRequired' ? 'May be required' : 'Not required'}{' · '}</>}
          {category.status}{' · '}{category.analyteCount} {displayFiltered ? 'reported ' : ''}analyte{category.analyteCount === 1 ? '' : 's'}
          {displayFiltered && <>{' · '}{selectedCount} displayed</>}
        </span>}
      </summary>
      {category.batches.length === 0 && <p>No reported analytes.</p>}
      <Batches category={category} issuanceId={issuanceId} index={index} selectedAnalyteKeys={selectedAnalyteKeys} hideTotals={hideTotals} />
    </details>
  );
}

function Batches({ category, issuanceId, index, selectedAnalyteKeys, hideTotals }: Props & DisplayProps & { category: BatchTestCategory }) {
  const batches = displayedBatches(category, selectedAnalyteKeys, hideTotals);
  const hiddenResultsNotice = hiddenAdverseResults(category, batches);
  if ((hideTotals || (category.kind === 'potency' && selectedAnalyteKeys !== undefined)) && category.batches.length > 0 && batches.length === 0) {
    return <>{hiddenResultsNotice}<p>{category.kind !== 'potency' || selectedAnalyteKeys === undefined ? 'No reported cannabinoid results are available for display.'
      : selectedAnalyteKeys.length === 0 ? 'No cannabinoid results are selected for display.'
      : 'No reported cannabinoid results match the display selection.'}</p></>;
  }
  return (
      <>{hiddenResultsNotice}
      <div className="rid-batch-results__batches">
      {batches.map(batch => (
        <article key={batch.key} className="rid-batch-results__batch" data-testid="landing-test-batch">
          <details data-testid="landing-test-batch-disclosure">
          <summary className="rid-batch-results__batch-header"><h5>
            {batch.name || 'Unnamed test batch'}
            {' - '}
            {batch.isRevoked === true ? 'Revoked' : batch.overallPassed === true ? 'Passed' : batch.overallPassed === false ? 'Failed' : 'Status unavailable'}
          </h5></summary>
          <div className="rid-batch-results__batch-content">
          <dl>
            <dt>Laboratory</dt><dd>{batch.laboratoryName || 'Unavailable'}</dd>
            <dt>License</dt><dd>{batch.laboratoryLicense || 'Unavailable'}</dd>
            <dt>Tested Date</dt><dd>{batch.testedDate ? formatDate(batch.testedDate) : 'Unavailable'}</dd>
          </dl>
          <details>
            <summary>Individual tests / analytes</summary>
            <div className="rid-batch-results__scroll" role="region" aria-label={`${category.name} analytes`} tabIndex={0}>
            <table>
              <thead><tr><th scope="col">Test / analyte</th>{category.kind !== 'potency' && <th scope="col">Status</th>}<th scope="col">Value</th><th scope="col">Unit</th><th scope="col">Qualifier</th><th scope="col">COA</th></tr></thead>
              <tbody>
                {batch.analytes.map(analyte => (
                  <tr key={analyte.key} data-testid="landing-batch-analyte">
                    <th scope="row">{analyteName(analyte)}</th>
                    {category.kind !== 'potency' && <td>{analyteStatus(analyte)}</td>}
                    <td data-testid="landing-batch-analyte-value">{analyteValue(analyte, category.kind === 'potency')}</td>
                    <td>{analyte.unit || 'Unavailable'}</td>
                    <td>{analyte.qualifier || 'Not reported'}</td>
                    <td data-testid="landing-batch-analyte-coa">
                      {analyte.documentId !== null && batch.documentIds.includes(analyte.documentId) && (
                        <a className="rid-batch-results__coa-link" href={getBatchTestDocumentUrl(issuanceId, index, analyte.documentId)} target="_blank" rel="noopener noreferrer">
                          COA {batch.documentIds.indexOf(analyte.documentId) + 1}
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </details>
          </div>
          </details>
        </article>
      ))}
      </div>
      </>
  );
}

export default function BatchTestResultsSection({ issuanceId, index, chevronSrc, labTestStatus, marketCode }: Props) {
  const results = usePublicBatchTestResults(issuanceId, index, marketCode);
  return <BatchTestResultsContent issuanceId={issuanceId} index={index} chevronSrc={chevronSrc} marketCode={marketCode}
    labTestStatus={resolvePublicLabTestStatus(results, labTestStatus)} results={results} />;
}

export function BatchTestResultsContent({ issuanceId, index, chevronSrc, labTestStatus, results, kindFilter, marketCode }: Props & { results: PublicBatchTestResultsState; kindFilter?: 'potency' | 'nonPotency' }) {
  const { data, isLoading, error, retry } = results;
  const selectedAnalyteKeys = displaySelection(data?.potencyDisplayPolicy, marketCode);
  const hideTotals = marketCode?.trim().toUpperCase() === 'CO';
  const groups = [
    { kind: 'contaminants', title: 'Contaminants' },
    { kind: 'other', title: 'Other test results' },
    { kind: 'potency', title: 'Potency' },
  ].filter(group => kindFilter === 'potency' ? group.kind === 'potency' : kindFilter === 'nonPotency' ? group.kind !== 'potency' : true);
  return (
    <ExpandedSection
      title={kindFilter === 'potency' ? 'Potency' : 'Batch Test Results'}
      defaultExpanded
      chevronSrc={chevronSrc}
      testId={kindFilter === 'potency' ? 'landing-potency-toggle' : 'landing-batch-test-results-toggle'}
      sectionTestId={kindFilter === 'potency' ? 'landing-potency-results' : 'landing-batch-test-results'}
      sectionClassName="rid-batch-results"
      cardClassName={data?.availability === 'available' || data?.availability === 'empty' ? 'rid-batch-results__cards' : undefined}
    >
        {labTestStatus?.trim() && <dl><dt>Lab Test Status</dt><dd data-testid="landing-batch-package-test-status">{labTestStatus}</dd></dl>}
        {isLoading && <p role="status">Loading batch test results...</p>}
        {error && <div role="alert"><p>{error}</p><button type="button" onClick={retry}>Retry batch test results</button></div>}
        {!isLoading && !error && (!issuanceId || data?.availability === 'unavailable') && <p>Batch test results are unavailable for this package.</p>}
        {!isLoading && !error && data?.availability === 'empty' && <p>No public lab results were reported for this package.</p>}
        {!isLoading && !error && data && data.omittedCount > 0 && (
          <p role="status">
            {data.omittedCount} lab result{data.omittedCount === 1 ? ' was' : 's were'} withheld because the reported data was incomplete or ambiguous.
          </p>
        )}
        {(data?.availability === 'available' || data?.availability === 'empty') && groups.map(group => {
          const categories = data.categories.filter(category => category.kind === group.kind);
          return categories.length > 0 ? (
            <div key={group.kind} className="rid-expanded__card" data-testid={`landing-batch-${group.kind}`}>
              {group.kind !== 'potency' && <h3>{group.title}</h3>}
              {categories.map(category => !data.matrixVersion && (group.kind === 'potency' || category.name === 'Other test results' || category.name === 'Category Unavailable')
                ? <Batches key={category.name} category={category} issuanceId={issuanceId} index={index} selectedAnalyteKeys={selectedAnalyteKeys} hideTotals={hideTotals} />
                : <Category key={category.name} category={category} issuanceId={issuanceId} index={index} selectedAnalyteKeys={selectedAnalyteKeys} hideTotals={hideTotals} />)}
            </div>
          ) : null;
        })}
    </ExpandedSection>
  );
}
